import './SaveCreatorSetModal.scss';
import { useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import {
  CreatorFilterState,
  YouTubeChannelInsights
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorInsights';
import {
  SaveCreatorSetInput,
  YouTubeCreatorSetWithMembers
} from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import { Button } from '@Components/Button';
import { Appearance, Design } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import { SegmentedControlButton } from '@Components/SegmentedControlButton';
import { Spinner } from '@Components/Spinner';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { Mixpanel, MxE } from '@src/utils/mixpanelWrapper';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import { CREATOR_SET_ENTITY } from '../CreatorSets/creator-sets-constants';
import {
  isNameTakenError,
  isSetNameTaken,
  toAddMembersInput,
  toNewMembers,
  withoutMembersOf
} from '../CreatorSets/creator-sets-utils';
import {
  lookUpChannels,
  useCreatorSets,
  useSaveCreatorSet
} from '../CreatorSets/hooks/use-creator-sets';
import { PAGE_TITLE } from '../youtube-planning-constants';
import { GoogleAdsAccount } from '../youtube-planning-types';
import { SAVE_TARGET_OPTIONS, SaveTarget, SaveTargetValue } from './creator-discovery-constants';

interface SaveCreatorSetModalProps {
  // The selected search results, in the order they were selected.
  creators: YouTubeChannelInsights[];
  search: CreatorFilterState;
  adsAccount: GoogleAdsAccount;
  onSaved: (set: YouTubeCreatorSetWithMembers, added: number) => void;
  onHide: () => void;
}

// Saves the selected creators to a new set or appends them to an existing one. Searches carry no
// audience data, so each creator is looked up first; that takes a few seconds per 20 creators.
export const SaveCreatorSetModal = ({
  creators,
  search,
  adsAccount,
  onSaved,
  onHide
}: SaveCreatorSetModalProps) => {
  const { cid } = useCompanyInfo();
  const setError = useSetError();
  const {
    data: loadedSets = [],
    isLoading: setsLoading,
    refetch: refetchSets
  } = useCreatorSets(cid);
  const { mutateAsync: saveSet } = useSaveCreatorSet(cid);

  const [target, setTarget] = useState<SaveTargetValue>(SaveTarget.NEW);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [existingSetId, setExistingSetId] = useState<string | null>(null);
  // The server's message for a rejected name, shown under the name field.
  const [serverNameError, setServerNameError] = useState<string>();
  // How many creators have been looked up so far; undefined when not saving.
  const [lookedUp, setLookedUp] = useState<number>();
  const [saving, setSaving] = useState(false);
  // The sets as they were when the save started. The save puts its result into the cached list
  // before it resolves, so without this the modal would check against the set it is saving: "A set
  // named … already exists", or "Every selected creator is already in this set".
  const [setsAtSave, setSetsAtSave] = useState<typeof loadedSets>();
  const sets = setsAtSave ?? loadedSets;

  // The picked set is kept while "New set" is showing, but only counts on the "Existing set" side.
  const existingSet =
    target === SaveTarget.EXISTING ? sets.find(set => String(set.id) === existingSetId) : undefined;
  const toAdd = withoutMembersOf(creators, existingSet);
  const alreadyInSet = creators.length - toAdd.length;
  const nameTaken = isSetNameTaken(sets, name);
  const nameError =
    serverNameError ?? (nameTaken ? `A set named "${name.trim()}" already exists.` : undefined);

  const canSave =
    toAdd.length > 0 &&
    (target === SaveTarget.NEW ? Boolean(name.trim()) && !nameError : Boolean(existingSet));

  const save = async () => {
    setSaving(true);
    setSetsAtSave(loadedSets);
    try {
      setLookedUp(0);
      const lookedUpById = await lookUpChannels(
        cid,
        adsAccount,
        toAdd.map(channel => channel.youtubeChannel.channelId),
        setLookedUp
      );
      const snapshottedAt = new Date().toISOString();
      // A save replaces the whole set, so an existing set is re-read after the lookup: changes made
      // to it while that ran are kept rather than overwritten.
      const latestSet = existingSet ? await getLatestSet(existingSet.id) : undefined;
      const newMembers = toNewMembers(
        withoutMembersOf(toAdd, latestSet),
        lookedUpById,
        snapshottedAt,
        latestSet?.members.length
      );
      const input: SaveCreatorSetInput = latestSet
        ? toAddMembersInput(latestSet, newMembers)
        : {
            company: cid,
            name: name.trim(),
            description: description.trim() || null,
            search_params: search,
            members: newMembers
          };
      const saved = await saveSet(input);
      Mixpanel.track(existingSet ? MxE.RecordSaved : MxE.RecordCreated, {
        page: PAGE_TITLE,
        entity: CREATOR_SET_ENTITY
      });
      onSaved(saved, newMembers.length);
    } catch (e) {
      // A name taken since the list loaded is shown under the name field; anything else goes to the
      // error modal.
      if (!existingSet && isNameTakenError(e)) {
        setServerNameError(e.message);
      } else {
        setError({ title: "Couldn't save creator set", message: e.message, reportError: e });
      }
    } finally {
      setSaving(false);
      setSetsAtSave(undefined);
      setLookedUp(undefined);
    }
  };

  const getLatestSet = async (id: number): Promise<YouTubeCreatorSetWithMembers> => {
    const { data: latest } = await refetchSets({ throwOnError: true });
    const set = latest?.find(candidate => candidate.id === id);
    if (!set) throw new Error('This creator set was deleted while saving.');
    return set;
  };

  return (
    <Modal
      show
      centered
      onHide={saving ? () => {} : onHide}
      className='save-creator-set-modal'
      data-testid='save-creator-set-modal'
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          Save {conditionallyPluralizeWithCount(creators.length, 'creator')} to a set
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SegmentedControlButton
          options={SAVE_TARGET_OPTIONS}
          selectedOption={target}
          disabled={saving}
          onChange={setTarget}
        />
        {target === SaveTarget.NEW ? (
          <NewSetFields
            name={name}
            description={description}
            nameError={nameError}
            disabled={saving}
            onNameChange={nextName => {
              setName(nextName);
              setServerNameError(undefined);
            }}
            onDescriptionChange={setDescription}
          />
        ) : (
          <ExistingSetField
            sets={sets}
            isLoading={setsLoading}
            existingSetId={existingSetId}
            alreadyInSet={alreadyInSet}
            allAlreadyInSet={Boolean(existingSet) && toAdd.length === 0}
            disabled={saving}
            onChange={setExistingSetId}
          />
        )}
        {lookedUp !== undefined && (
          <div className='save-creator-set-progress' role='status'>
            <SaveProgress lookedUp={lookedUp} total={toAdd.length} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          disabled={saving}
          onClick={onHide}
        >
          Cancel
        </Button>
        <Button disabled={!canSave} loading={saving} onClick={save}>
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

interface NewSetFieldsProps {
  name: string;
  description: string;
  nameError?: string;
  disabled: boolean;
  onNameChange: (name: string) => void;
  onDescriptionChange: (description: string) => void;
}

const NewSetFields = ({
  name,
  description,
  nameError,
  disabled,
  onNameChange,
  onDescriptionChange
}: NewSetFieldsProps) => (
  <div className='save-creator-set-fields'>
    <Form.Group controlId='creator-set-name'>
      <Form.Label>Name</Form.Label>
      <Form.Control
        value={name}
        placeholder='e.g. Q4 gardening creators'
        isInvalid={Boolean(nameError)}
        disabled={disabled}
        onChange={e => onNameChange(e.target.value)}
      />
      <Form.Control.Feedback type='invalid'>{nameError}</Form.Control.Feedback>
    </Form.Group>
    <Form.Group controlId='creator-set-description'>
      <Form.Label>Description (optional)</Form.Label>
      <Form.Control
        as='textarea'
        rows={2}
        value={description}
        disabled={disabled}
        onChange={e => onDescriptionChange(e.target.value)}
      />
    </Form.Group>
  </div>
);

interface ExistingSetFieldProps {
  sets: YouTubeCreatorSetWithMembers[];
  isLoading: boolean;
  existingSetId: string | null;
  alreadyInSet: number;
  allAlreadyInSet: boolean;
  disabled: boolean;
  onChange: (setId: string | null) => void;
}

const ExistingSetField = ({
  sets,
  isLoading,
  existingSetId,
  alreadyInSet,
  allAlreadyInSet,
  disabled,
  onChange
}: ExistingSetFieldProps) => {
  if (isLoading) return <Spinner />;
  if (sets.length === 0) {
    return <div className='save-creator-set-note'>This company has no creator sets yet.</div>;
  }
  return (
    <div className='save-creator-set-fields'>
      <Form.Group>
        <Form.Label>Creator set</Form.Label>
        <Dropdown
          searchable
          placeholder='Choose a set'
          options={sets.map(set => ({
            value: String(set.id),
            label: `${set.name} (${conditionallyPluralizeWithCount(set.members.length, 'creator')})`
          }))}
          value={existingSetId}
          disabled={disabled}
          onChange={onChange}
        />
      </Form.Group>
      {alreadyInSet > 0 && (
        <div className='save-creator-set-note'>
          {allAlreadyInSet
            ? 'Every selected creator is already in this set.'
            : `${conditionallyPluralizeWithCount(alreadyInSet, 'creator')} already in this set ${
                alreadyInSet === 1 ? 'is' : 'are'
              } skipped.`}
        </div>
      )}
    </div>
  );
};

interface SaveProgressProps {
  lookedUp: number;
  total: number;
}

// Most saves fit in one lookup batch, so the count only shows once a batch has come back; before
// that it would sit at "0 of N" for the whole lookup.
const SaveProgress = ({ lookedUp, total }: SaveProgressProps) => {
  if (lookedUp >= total) return 'Saving…';
  if (lookedUp === 0) return 'Looking up creator details…';
  return `Looking up creator details: ${lookedUp} of ${total}…`;
};
